// The guided tour of desk booking, for people arriving from Team Today.
//
// Steps point at the real screen via [data-tour] attributes on MyBookingsPage,
// in the order the page reads: days, whereabouts, offices, the plan, the
// extras, then cancelling. The engine (shared/FeatureTour.jsx) and the
// show-once record (hooks/useFeatureTour.js) are generic; only this file knows
// about desks. A tour for the next module onboarded onto the platform is
// another file like this one.
//
// Wording note: several targets only render with data behind them (favourites,
// upcoming bookings). The engine shows those steps centred when the element is
// missing, so each body is written to read standalone as well as pointed.

export const DESK_BOOKING_TOUR_ID = 'desk-booking';

// The extras live in a <details>; open it so its step has something to point
// at. Left open afterwards — closing it mid-read would be worse.
const openExtras = () => {
  const el = document.querySelector('details[data-tour-container="extras"]');
  if (el && !el.open) el.open = true;
};

export const deskBookingTourSteps = [
  {
    title: 'Welcome to Bookings',
    body: [
      'Desk booking now lives here instead of Team Today. This one-minute tour shows you how to book a desk, save your favourites, book several days at once, and cancel.',
      'Use Next (or the arrow keys) to walk through, or Skip if you would rather explore. Either way it will not pop up again.',
    ],
  },
  {
    title: 'Pick your day',
    target: '[data-tour="week-strip"]',
    body: [
      'Your week, one tile per day, showing what you have set and any desk you hold. Click a tile to work on that day — everything below follows it.',
      'The ‹ › arrows move a week at a time and Today brings you back. Days too far ahead are dimmed: bookings open three weeks out.',
    ],
  },
  {
    title: 'Book several days at once',
    target: '[data-tour="multi-day"]',
    body: [
      'Tick "Select several days", then click as many day tiles as you like. Booking a desk, or setting where you are, then applies to every selected day in one go.',
      'Handy for booking your regular pattern for the whole week in a single click.',
    ],
  },
  {
    title: 'Repeat a whole week',
    target: '[data-tour="repeat-week"]',
    body: [
      'Once a week is planned, "Repeat week" does it again for the weeks ahead: your desks and your working-from-home days both carry forward.',
      'It asks how many weeks, and only offers the ones that fit inside the booking window. A day someone else has already taken is skipped, and named afterwards.',
    ],
  },
  {
    title: 'Say where you are',
    target: '[data-tour="whereabouts"]',
    body: [
      'A day with nothing set shows a + in the middle of its tile. Click the + and pick working from home, offsite or non working day, so your team can plan around you.',
      'The menu asks how much of the day: all day, the morning or the afternoon. A morning at home and an afternoon at a desk is two answers on one tile, and the planner shows both.',
      'Being at an office with desks means holding one, so “Book a desk” takes you straight to the seat picker for that day. An office with no desks to book sits alongside the other answers, one click.',
      'Once a day is set, the ✕ in the corner of the tile clears it again, and the icon can be clicked to change your answer. On a day you have booked a desk, that ✕ cancels the desk too, since the booking is what is saying where you are.',
      'Work from home on the same day every week? Tick it under “Working from home every week” in the same menu. It shows as home on the planner unless you answer that day differently, and if you have a reserved desk it opens to others on that day a week at a time: each Monday opens the following week.',
      'If you work part-time, the days you do not work (as HR record them) show as not working, and a reserved desk is free to others on them.',
      'If a PLOW puts you on a working away job, your reserved desk is free to others on those days, a week at a time: each Monday opens that week.',
      'Booking a desk sets "in the office" for you automatically — and setting anything other than an office releases any desk you were holding that day.',
    ],
  },
  {
    title: 'Choose an office',
    target: '[data-tour="offices"]',
    body: [
      'One card per office, with how many desks are free and how many of your team are already in — the two numbers that actually decide where you go.',
    ],
  },
  {
    title: 'All day, or half a day',
    target: '[data-tour="desk-controls"]',
    body: [
      'Book the full day or just the morning or afternoon — an AM booking leaves the desk free for someone else’s PM.',
      'The two halves can be at different offices, so a morning at PP and an afternoon at RW is one day booked twice.',
      '"Free only" hides desks you cannot have, and List swaps the floor plan for a plain list — easier on a phone.',
    ],
  },
  {
    title: 'Pick a seat',
    target: '[data-tour="floor-plan"]',
    body: [
      'Tap any green desk and a small card opens on it: Book all day, AM or PM, then confirm. Desks where your teammates are sitting are highlighted, and hovering a taken desk tells you who is on it.',
      'Pick a second desk for a slot you already have one in and the dialog offers to switch you instead, releasing the first.',
      'Prefer names to maps? The "Choose a desk" dropdown at the top lists every desk and its state, grouped by area.',
    ],
  },
  {
    title: 'Favourite your usual desks',
    target: '[data-tour="favourite-desks"]',
    before: openExtras,
    body: [
      'Star up to three desks you sit at most. They appear as one-click "Your desks" buttons above the plan, so a normal morning is: open Bookings, click your desk, confirm.',
      'You can also star a desk from the card that opens when you tap it on the plan.',
    ],
  },
  {
    title: 'Repeat weekly, and guests',
    target: '[data-tour="repeat-guest"]',
    before: openExtras,
    body: [
      'In the office the same days every week? Tick "Repeat weekly", choose the weekdays and an end date, and the booking repeats for you.',
      'Expecting a visitor? Put their name in the guest box before booking and the seat is held under their name, not yours.',
    ],
  },
  {
    // Admin, HR and PMO only, and the control is not rendered for anybody
    // else, so for everybody else this step had nothing to point at: it fell
    // back to centred and spent one step of a short tour on a dropdown they
    // will never see. Gated on the same capability the page uses to decide
    // whether to render the control, not on a list of role names.
    title: 'Booking for somebody else',
    target: '[data-tour="booking-for"]',
    when: (c) => c.canBookForOthers,
    body: [
      'Your role (admin, HR or PMO) lets you take a seat on behalf of a colleague. Choose their name in the "Book for someone" dropdown at the top of the page, press Start, and the whole page becomes theirs: their week, their reserved desk, their team highlighted on the plan.',
      'Nothing happens until you press Start, and an amber bar across the top names whoever you are booking for until you press "Book for myself again". They are emailed anything you book or cancel.',
      'It covers desks only, so it ends when you leave this tab, and saying where somebody is working stays theirs to answer.',
    ],
  },
  {
    title: 'Cancel or release a booking',
    target: '[data-tour="my-upcoming"]',
    body: [
      'Every booking you hold is listed under "My upcoming bookings" with a Cancel next to it — and "Cancel series" to drop a weekly repeat in one go.',
      'Quicker still: click a desk you already hold, on the plan or in the list, and it is released for the selected days.',
    ],
  },
  {
    title: 'Jump further ahead',
    target: '[data-tour="mini-calendar"]',
    body: [
      'The month calendar jumps you to any date in the booking window, and a dot marks each day you already have something booked.',
    ],
  },
  {
    title: 'Vans and your team',
    target: '[data-tour="tabs"]',
    body: [
      'The Vans tab books a company van the same way — pick dates, pick a van, say where it is going.',
      'The Team tab shows the whole company’s week at a glance, filterable by team, so you can see who will be in before you decide.',
      'Anyone a PLOW has on a working away job shows as offsite that day, with the job named, unless they have said something else. That includes you.',
    ],
  },
  {
    title: 'That’s everything',
    body: [
      'You are ready to book. If you want this walkthrough again, the "Tour" button at the top of the page replays it any time.',
    ],
  },
];
